import { useRef } from "react";
import { Download, FileText, LoaderCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PROFILE_FORM_STYLES } from "@/components/Forms/profile-form-styles";
import PDFViewer from "@/components/PDFViewer/PDFViewer";
import ImageViewer from "@/components/MediaViewer/ImageViewer";
import { cn } from "@/lib/utils";
import type { EvidencePreview } from "../license-verification/use-open-verification-evidence";
import { evidencePreviewFormat } from "./evidence-preview-content";

export function VerificationEvidencePreview({
  preview,
  onClose,
  onRetry,
  onDownload,
}: {
  preview: EvidencePreview | null;
  onClose: () => void;
  onRetry: () => void;
  onDownload: () => void;
}) {
  const openerRef = useRef<HTMLElement | null>(null);
  if (!preview) {
    return null;
  }
  const {
    metadata,
    selection,
    isLoading,
    isDownloading,
    error,
    downloadError,
    text,
  } = preview;
  const format = metadata ? evidencePreviewFormat(metadata.mediaType) : null;
  const viewerHeight = "h-full min-h-0 rounded-none";
  let content;
  if (error) {
    content = (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center text-sm">
        <p role="alert" className="text-destructive">
          {error}
        </p>
        <Button variant="outline" onClick={onRetry}>
          Retry preview
        </Button>
      </div>
    );
  } else if (isLoading || !metadata) {
    content = (
      <div
        role="status"
        className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground"
      >
        <LoaderCircle className="size-5 animate-spin" aria-hidden="true" />
        Loading evidence…
      </div>
    );
  } else if (format === "pdf") {
    content = (
      <PDFViewer
        key={metadata.url}
        url={metadata.url}
        fitPage
        containerClassName={viewerHeight}
      />
    );
  } else if (format === "image") {
    content = (
      <ImageViewer
        key={metadata.url}
        url={metadata.url}
        alt={selection.target.label}
        containerClassName={viewerHeight}
      />
    );
  } else if (format === "text" && text) {
    content = (
      <div className="flex h-full min-h-0 flex-col">
        {text.truncated && (
          <p
            role="status"
            className="border-b bg-amber-50 px-5 py-3 text-sm text-amber-900"
          >
            Preview truncated to the first 1 MB. Download the full file to see
            all source data.
          </p>
        )}
        <pre
          aria-label="Evidence content"
          tabIndex={0}
          className="min-h-0 flex-1 overflow-auto whitespace-pre-wrap break-all p-5 text-xs leading-relaxed text-slate-800"
        >
          {text.content}
        </pre>
      </div>
    );
  } else {
    content = (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center text-sm text-muted-foreground">
        <FileText className="size-10" aria-hidden="true" />
        <p>
          This file type cannot be previewed here. Download the original to open
          it.
        </p>
      </div>
    );
  }
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        // Keep credential evidence out of Sentry session replays.
        data-sentry-block
        onOpenAutoFocus={() => {
          if (document.activeElement instanceof HTMLElement) {
            openerRef.current = document.activeElement;
          }
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          const parent = Array.from(
            document.querySelectorAll<HTMLElement>(
              '[role="dialog"][data-state="open"]',
            ),
          ).at(-1);
          if (
            openerRef.current?.isConnected &&
            (!parent || parent.contains(openerRef.current))
          ) {
            openerRef.current.focus();
          } else {
            parent?.focus();
          }
        }}
        className={cn(
          PROFILE_FORM_STYLES.dialog,
          "[&.dialog-content]:h-[90dvh] [&.dialog-content]:max-h-[90dvh] sm:[&.dialog-content]:max-w-6xl",
        )}
      >
        <DialogHeader
          className={cn(
            PROFILE_FORM_STYLES.header,
            "px-5 py-5 pr-14 sm:px-6 sm:pr-16",
          )}
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className={PROFILE_FORM_STYLES.eyebrow}>Evidence preview</p>
              <DialogTitle className="break-words text-left text-lg font-semibold text-slate-900">
                {selection.target.label}
              </DialogTitle>
              <DialogDescription className="mt-1 text-left text-xs text-slate-500">
                Retained evidence from this primary source check.
              </DialogDescription>
            </div>
            {(format === "image" || format === "pdf") && !isLoading && (
              <Button type="button" variant="ghost" size="sm" onClick={onRetry}>
                Reload preview
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-2"
              disabled={!metadata || isDownloading}
              onClick={onDownload}
            >
              {isDownloading ? (
                <LoaderCircle
                  className="size-4 animate-spin"
                  aria-hidden="true"
                />
              ) : (
                <Download className="size-4" aria-hidden="true" />
              )}
              {isDownloading ? "Downloading…" : "Download"}
            </Button>
          </div>
          {downloadError && (
            <p role="alert" className="text-left text-sm text-destructive">
              {downloadError}
            </p>
          )}
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-hidden bg-slate-50">
          {content}
        </div>
      </DialogContent>
    </Dialog>
  );
}
